import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from 'react';

import { getHasSeenPreview, setHasSeenPreview } from '../core/storage/previewStorage';
import type {
  DollarAllocationBucket,
  SampleNetWorth,
  SampleProfile,
  SampleProjections,
  SampleSummaries,
  SpendCategoryNode,
} from '../types/financial';
import { useAuth } from './AuthContext';
import { useHousehold, useHouseholdDerived } from './HouseholdContext';

type SampleDataContextValue = {
  isSampleMode: boolean;
  hasSeenPreview: boolean;
  isHydrated: boolean;
  sampleProfile: SampleProfile;
  sampleSummaries: SampleSummaries;
  sampleDollarAllocation: DollarAllocationBucket[];
  sampleNetWorth: SampleNetWorth;
  sampleSpendTree: SpendCategoryNode[];
  sampleProjections: SampleProjections;
  enterSampleMode: () => void;
  exitSampleMode: () => void;
  markPreviewSeen: () => Promise<void>;
  resetPreviewSeen: () => Promise<void>;
  /** Re-read household core from storage and re-derive. */
  refreshHousehold: () => Promise<void>;
};

const SampleDataContext = createContext<SampleDataContextValue | null>(null);

/**
 * Thin façade over HouseholdStore for sample-mode flags + default derived view.
 * Rollups always come from `deriveHousehold` via the household store — never from disk.
 */
export function SampleDataProvider({ children }: PropsWithChildren) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { isHydrated: householdHydrated, refresh, resetToFixture } = useHousehold();
  const derived = useHouseholdDerived();

  /** Sample data is opt-in (Explore). Do not front the demo on launch. */
  const [isSampleMode, setIsSampleMode] = useState(false);
  const [hasSeenPreview, setHasSeenPreviewState] = useState(false);
  const [previewHydrated, setPreviewHydrated] = useState(false);

  useEffect(() => {
    let mounted = true;
    getHasSeenPreview().then((seen) => {
      if (mounted) {
        setHasSeenPreviewState(seen);
        setPreviewHydrated(true);
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (authLoading || !householdHydrated) return;
    if (isAuthenticated) {
      setIsSampleMode(false);
    }
  }, [authLoading, isAuthenticated, householdHydrated]);

  const enterSampleMode = useCallback(() => {
    setIsSampleMode(true);
    void resetToFixture();
  }, [resetToFixture]);
  const exitSampleMode = useCallback(() => setIsSampleMode(false), []);

  const markPreviewSeen = useCallback(async () => {
    await setHasSeenPreview(true);
    setHasSeenPreviewState(true);
  }, []);

  const resetPreviewSeen = useCallback(async () => {
    await setHasSeenPreview(false);
    setHasSeenPreviewState(false);
  }, []);

  const value = useMemo<SampleDataContextValue>(
    () => ({
      isSampleMode,
      hasSeenPreview,
      isHydrated: previewHydrated && householdHydrated,
      sampleProfile: derived.profile,
      sampleSummaries: derived.summaries,
      sampleDollarAllocation: derived.dollarAllocation,
      sampleNetWorth: derived.netWorth,
      sampleSpendTree: derived.spendTree,
      sampleProjections: derived.projections,
      enterSampleMode,
      exitSampleMode,
      markPreviewSeen,
      resetPreviewSeen,
      refreshHousehold: refresh,
    }),
    [
      isSampleMode,
      hasSeenPreview,
      previewHydrated,
      householdHydrated,
      derived,
      enterSampleMode,
      exitSampleMode,
      markPreviewSeen,
      resetPreviewSeen,
      refresh,
    ],
  );

  return <SampleDataContext.Provider value={value}>{children}</SampleDataContext.Provider>;
}

export function useSampleData(): SampleDataContextValue {
  const ctx = useContext(SampleDataContext);
  if (!ctx) {
    throw new Error('useSampleData must be used within SampleDataProvider');
  }
  return ctx;
}
